/** Step 1: filter email templates by `createdDate` (backend BETWEEN_TS). */
export type TemplateDatePreset =
  | "all"
  | "today"
  | "last-3-days"
  | "last-7-days"
  | "last-week"
  | "last-month"
  | "current-year"
  | "year-2025"
  | "year-2024"
  | "year-2023"
  | "year-2022"
  | "custom";

export interface SendEmailFormData {
  templateDatePreset: TemplateDatePreset;
  /** `YYYY-MM-DD` for custom range (local calendar day). */
  templateDateFrom: string;
  templateDateTo: string;
  emailTemplateId: string;
  recipientListId: string;
  /** WSG firm member person ids for Test List sends (independent from distribution list). */
  testListMemberIds: string[];
}

export type EmailActionType = "schedule" | "send";
export type TimeZoneType = "regional" | "est";

export interface ActionFormData {
  actionType: EmailActionType;
  scheduledDate: string;
  scheduledTime: string;
  timeZone: TimeZoneType;
}

export interface FormErrors {
  emailTemplate: boolean;
}

export type ConditionOperator = "is" | "is-one-of" | "is-not" | "like";
export type ConditionInputType = "select" | "text";
export type ConditionValueType = "id" | "enum" | "string";

export interface ConditionFieldConfig {
  id: string;
  name: string;
  backendField: string;
  inputType: ConditionInputType;
  valueType: ConditionValueType;
  operators: ConditionOperator[];
  allowMultiValueForIs: boolean;
  placeholder?: string;
  query?: string;
  note?: string;
}

export interface ConditionValueOption {
  id: string;
  name: string;
}

export interface RecipientCondition {
  id: string;
  fieldId: string;
  conditionOperator: ConditionOperator;
  valueId: string;
}

export type Operator = "AND" | "OR";

export interface RecipientConditionGroup {
  id: string;
  logic: Operator;
  conditions: RecipientCondition[];
}

export interface RecipientSelection {
  /** Default join operator when `groupConnectors` is missing (legacy saved lists). */
  logic: Operator;
  groups: RecipientConditionGroup[];
  /** Join operator between groups[i] and groups[i + 1]. Length = groups.length - 1. */
  groupConnectors: Operator[];
}

export function normalizeGroupConnectors(
  groups: RecipientConditionGroup[],
  logic: Operator,
  groupConnectors?: Operator[],
): Operator[] {
  const expectedLength = Math.max(0, groups.length - 1);
  if (groupConnectors?.length === expectedLength) {
    return [...groupConnectors];
  }
  return Array.from({ length: expectedLength }, () => logic);
}

export interface EmailTemplate {
  id: string;
  emailNumber?: string;
  name: string;
  title?: string;
  year: string;
  htmlContent?: string;
  subject?: string;
  sender?: string;
  fromEmail?: string;
  category?: string;
  emailType?: string;
  emailEventId?: string;
  attachment?: string;
}
